import Box from "@mui/material/Box";
import { Formik } from "formik";
import * as yup from "yup";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { CountryCode, isValidPhoneNumber } from "libphonenumber-js";
import { getCountryCodes, preventSpecialCharacters } from "../../../utils/utilityFunctions";
import { MFSubmitButton, MFTextField } from "../../../lib/formik";
import { CountryCodesDropDown, LoginLayout } from "../../commonComponents";
import { Alert, CircularProgress, Typography } from "@mui/material";
import { RootStateType } from "../../../redux-store/reducers";
import { sendOtpPayload } from "../../../redux-store/types/api-types";
import { SendOtp } from "../../../redux-store/actions/userManagement";

interface Values {
    mobile: string;
    countryNameAndCode: string;
    email: string;
}

const validationSchema = yup.object().shape({
    mobile: yup.string().test("mobile", (value, context) => {
        const { createError, path, parent } = context;
        const codesList = getCountryCodes()
            .filter((codes) => codes.label === parent.countryNameAndCode)
            .map((codes) => codes.countryCode)
            .toString();

        if (!value) {
            return createError({ message: "Mobile number is required", path });
        }
        if (!isValidPhoneNumber(value, codesList as CountryCode)) {
            return createError({ message: "Invalid Mobile number", path });
        }
        return true;
    }),
    countryNameAndCode: yup.string().required("Country Code is required"),
    email: yup.string().email("Invalid email").required("Email is required"),
});

// Helper function to prepare the cryptographic key for decryption.
const getCryptoKey = async (secret: string) => {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(secret);
    return await window.crypto.subtle.importKey(
        'raw',
        keyData,
        { name: 'AES-GCM', length: 256 },
        true,
        ['encrypt', 'decrypt']
    );
};

const InvestorRiskLogin = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const { encryptedData } = useParams<{ encryptedData: string }>();

    // State to hold the final decrypted data, which also controls if fields are disabled.
    const [decryptedData, setDecryptedData] = useState<any>(null);
    const [mobileElementWidth, setMobileElementWidth] = useState<number | null>(null);

    // State for UI feedback
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    // Initial values for the Formik form, will be updated after decryption.
    const [initialValues, setInitialValues] = useState<Values>({
        mobile: "",
        countryNameAndCode: "India: +91",
        email: "",
    });

    const fromBase64Url = (base64Url: string): Uint8Array => {
        let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        // Add padding back if necessary
        while (base64.length % 4) {
            base64 += '=';
        }
        const binaryString = atob(base64);
        return Uint8Array.from(binaryString, c => c.charCodeAt(0));
    };

    // This useEffect hook runs once on component mount to handle decryption.
    useEffect(() => {
        const decryptAndSetData = async () => {
            if (!encryptedData) {
                setError("No data provided in the URL.");
                setIsLoading(false);
                return;
            }

            const secretKey = 'A?D(G+KbPeShVmYq3t6w9z$C&F)J@NcQ';

            try {
                const combinedBuffer = fromBase64Url(encryptedData);
                const iv = combinedBuffer.slice(0, 12);
                const data = combinedBuffer.slice(12);

                if (iv.length !== 12) throw new Error("Decryption failed: Invalid data format.");

                const cryptoKey = await getCryptoKey(secretKey);
                const decryptedBuffer = await window.crypto.subtle.decrypt(
                    { name: 'AES-GCM', iv: iv },
                    cryptoKey,
                    data
                );

                const decoder = new TextDecoder();
                const jsonString = decoder.decode(decryptedBuffer);
                const payload = JSON.parse(jsonString);

                // Assuming pan is also in the payload
                if (payload.mobile && payload.email && payload.pan) {
                    setInitialValues({
                        mobile: payload.mobile,
                        email: payload.email,
                        countryNameAndCode: "India: +91",
                    });
                    setDecryptedData(payload);
                } else {
                    throw new Error("Decrypted data is missing required fields (mobile, email, or pan).");
                }
            } catch (err) {
                console.error('Decryption failed:', err);
                setError('Failed to process the information. The link may be invalid or expired.');
            } finally {
                setIsLoading(false);
            }
        };

        decryptAndSetData();
    }, [encryptedData]);

    useEffect(() => {
        const mobileElement = document.getElementsByName("mobile");
        setMobileElementWidth(mobileElement[0]?.parentElement?.clientWidth as number);
    }, []);

    const handleSubmit = async (values: Values) => {
        if (!decryptedData) {
            setError("Decrypted data is not available. Cannot send OTP.");
            return;
        }

        const payload: sendOtpPayload = {
            pan: decryptedData.pan,
            emailId: values.email,
            mobileNumber: Number(values.mobile),
        };

        try {
            const response = await dispatch(SendOtp(payload)) as unknown as any;
            // Assuming a successful response has a "Status" field
            if (response && response.Status === "OTP Saved Successfully") {
                history.push('/investor-otp', { decryptedData });
            } else {
                // Handle cases where the API call is successful but the operation isn't
                setError(response?.message || "Failed to send OTP. Please try again.");
            }
        } catch (apiError) {
            // This will be caught by the redux middleware, the error will be in otpError
            console.error("Send OTP failed:", apiError);
        }
    };

    return (
        <LoginLayout LoginText="Login">
            <div>
                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Verifying details...</Typography>
                    </Box>
                )}

                {(error) && (
                    <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
                )}

                {!isLoading && !error && (
                    <Formik
                        enableReinitialize // This is crucial for updating the form with decrypted data
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                        validationSchema={validationSchema}
                    >
                        {({ handleSubmit, values }) => (
                            <Box
                                component="form"
                                noValidate
                                onSubmit={handleSubmit}
                                sx={{
                                    ".MuiLoadingButton-root": { mt: 5 },
                                    ".MuiInputLabel-root": {
                                        fontFamily: "Poppins, sans-serif",
                                        fontSize: 16,
                                        fontWeight: 500,
                                    },
                                }}
                            >
                                <MFTextField
                                    label="Mobile Number"
                                    name="mobile"
                                    placeholder="Enter Mobile Number"
                                    onKeyDown={(e) => preventSpecialCharacters(e)}
                                    startAdornment={
                                        <CountryCodesDropDown
                                            ElementWidth={mobileElementWidth}
                                            name="countryNameAndCode"
                                            value={values.countryNameAndCode}
                                            disabled={!!decryptedData}
                                        />
                                    }
                                    disabled={!!decryptedData}
                                />
                                <MFTextField
                                    label="Email ID"
                                    name="email"
                                    placeholder="Enter Email ID"
                                    disabled={!!decryptedData}
                                />
                                <MFSubmitButton
                                    label="Get OTP"
                                />
                            </Box>
                        )}
                    </Formik>
                )}
            </div>
        </LoginLayout>
    );
};

export default InvestorRiskLogin;